import React, { useState, useEffect } from "react";
import { Warehouse } from "../../types/Warehouse";
import Button from "../ui/Button";
import WarehouseItem from "./WarehouseItem";
import WarehouseForm from "./WarehouseForm";
import { useFetchContext } from "../../context/FetchContext";
import { useToast } from "../../context/ToastContext";
import { IDataPagination } from "types/User";

const WarehouseList: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(
    null
  );
  const {
    fetchData,
    state: { isLoading },
  } = useFetchContext();
  const { showToast } = useToast();

  // Fetch warehouses from API
  useEffect(() => {
    const getWarehouses = async () => {
      try {
        const response = await fetchData<IDataPagination<Warehouse>>(
          "/warehouses"
        );
        if (response && response.data && response.data.items) {
          setWarehouses(response.data.items);
        }
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        showToast("Errore durante il caricamento dei magazzini", "error");
      }
    };

    getWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddWarehouse = () => {
    setEditingWarehouse(null);
    setIsModalOpen(true);
  };

  const handleEditWarehouse = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    setIsModalOpen(true);
  };

  const handleSaveWarehouse = async (warehouse: Warehouse) => {
    try {
      if (editingWarehouse) {
        // Update existing warehouse
        const response = await fetchData<Warehouse>(
          `/warehouses/${warehouse.uuid}`,
          "PUT",
          warehouse
        );
        if (response && response.data) {
          setWarehouses(
            warehouses.map((w) =>
              w.uuid === response.data.uuid ? response.data : w
            )
          );
          showToast("Magazzino aggiornato con successo", "success");
        }
      } else {
        // Add new warehouse
        const response = await fetchData<Warehouse>(
          "/warehouses",
          "POST",
          warehouse
        );
        if (response && response.data) {
          setWarehouses([...warehouses, response.data]);
          showToast("Magazzino creato con successo", "success");
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving warehouse:", error);
      showToast("Errore durante il salvataggio del magazzino", "error");
    }
  };

  const handleDeleteWarehouse = async (uuid: string) => {
    try {
      await fetchData(`/warehouses/${uuid}`, "DELETE");
      setWarehouses(warehouses.filter((warehouse) => warehouse.uuid !== uuid));
      showToast("Magazzino eliminato con successo", "success");
    } catch (error) {
      console.error("Error deleting warehouse:", error);
      showToast("Errore durante l'eliminazione del magazzino", "error");
    }
  };

  return (
    <div className="overflow-hidden bg-white shadow sm:rounded-lg">
      <div className="flex justify-between items-center px-4 py-5 sm:px-6">
        <h2 className="text-lg font-medium text-gray-900">Lista Magazzini</h2>
        <Button onClick={handleAddWarehouse}>Aggiungi Magazzino</Button>
      </div>
      <div className="border-t border-gray-200">
        {isLoading ? (
          <div className="text-center py-4">
            <svg
              className="animate-spin h-8 w-8 mx-auto text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="mt-2 text-gray-500">Caricamento magazzini...</p>
          </div>
        ) : warehouses.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-500">Nessun magazzino trovato</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Nome
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    ID Esterno
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Email Notifiche
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Locazioni
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {warehouses.map((warehouse) => (
                  <WarehouseItem
                    key={warehouse.uuid}
                    warehouse={warehouse}
                    onEdit={() => handleEditWarehouse(warehouse)}
                    onDelete={() => handleDeleteWarehouse(warehouse.uuid || "")}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <WarehouseForm
          warehouse={editingWarehouse}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveWarehouse}
        />
      )}
    </div>
  );
};

export default WarehouseList;
